/**
 * SearchAutocomplete Component
 * Autocomplete con sugerencias fuzzy para búsqueda de productos
 */
import React, { useState, useEffect, useRef } from 'react';
import { Search, Package, Tag, Store, TrendingUp, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const SearchAutocomplete = ({
  className = '',
  placeholder = 'Buscar productos, tiendas...',
  onSearch,
  autoFocus = false,
  size = 'default', // 'default' | 'large'
  variant = 'default' // 'default' | 'header'
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch suggestions with debounce
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce API call
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get(`/marketplace/suggestions?q=${encodeURIComponent(query)}&limit=8`);
        setSuggestions(response.data);
        setShowDropdown(response.data.length > 0);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (searchQuery = query) => {
    if (!searchQuery.trim()) return;
    
    // Log search for analytics
    api.post(`/marketplace/search-log?q=${encodeURIComponent(searchQuery)}`).catch(() => {});
    
    setShowDropdown(false);
    
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      navigate(`/buscar?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'category' && suggestion.category_id) {
      navigate(`/buscar?category=${suggestion.category_id}`);
    } else if (suggestion.type === 'store') {
      navigate(`/buscar?q=${encodeURIComponent(suggestion.text)}`);
    } else {
      handleSearch(suggestion.text);
    }
    setShowDropdown(false);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) {
      if (e.key === 'Enter') {
        handleSearch();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'product':
        return <Package className="w-4 h-4 text-slate-400" />;
      case 'category':
        return <Tag className="w-4 h-4 text-purple-500" />;
      case 'store':
        return <Store className="w-4 h-4 text-blue-500" />;
      case 'popular':
        return <TrendingUp className="w-4 h-4 text-orange-500" />;
      default:
        return <Search className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'product': return 'Producto';
      case 'category': return 'Categoría';
      case 'store': return 'Tienda';
      case 'popular': return 'Popular';
      default: return '';
    }
  };

  const isLarge = size === 'large';
  const isHeader = variant === 'header';

  return (
    <div className={`relative ${className}`}>
      <div className="relative flex">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={`w-full ${isLarge ? 'pl-12 pr-12 py-4 text-lg' : 'pl-10 py-2.5 text-sm'} ${
            isHeader
              ? 'bg-white rounded-l-lg border-0 focus:ring-0 pr-4 text-slate-800 placeholder-slate-400'
              : `${isLarge ? 'pr-12' : 'pr-10'} rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100`
          } transition-all outline-none`}
          data-testid="search-autocomplete-input"
        />

        {/* Search button for header variant */}
        {isHeader && (
          <button
            onClick={() => handleSearch()}
            className="flex items-center justify-center px-4 rounded-r-lg transition-colors hover:opacity-90"
            style={{ backgroundColor: '#F37322' }}
          >
            <Search className="w-5 h-5 text-white" />
          </button>
        )}

        <div className={`absolute left-0 top-0 ${isLarge ? 'pl-4' : 'pl-3'} h-full flex items-center pointer-events-none`}>
          {loading ? (
            <Loader2 className={`${isLarge ? 'w-5 h-5' : 'w-4 h-4'} text-slate-400 animate-spin`} />
          ) : (
            <Search className={`${isLarge ? 'w-5 h-5' : 'w-4 h-4'} text-slate-400`} />
          )}
        </div>

        {query && (
          <button
            onClick={() => { setQuery(''); setSuggestions([]); setShowDropdown(false); }}
            className={`absolute ${isHeader ? 'right-12' : 'right-0'} top-0 ${isLarge ? 'pr-4' : 'pr-3'} h-full flex items-center text-slate-400 hover:text-slate-600`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div 
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50"
          data-testid="search-suggestions-dropdown"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.type}-${suggestion.text}-${index}`}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors ${
                selectedIndex === index ? 'bg-slate-100' : ''
              }`}
              data-testid={`suggestion-${index}`}
            >
              {getIcon(suggestion.type)}
              <div className="flex-1 min-w-0">
                <span className="text-slate-800 block truncate">{suggestion.text}</span>
                {suggestion.category && (
                  <span className="text-xs text-slate-400">en {suggestion.category}</span>
                )}
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                suggestion.type === 'category' ? 'bg-purple-100 text-purple-600' :
                suggestion.type === 'store' ? 'bg-blue-100 text-blue-600' :
                suggestion.type === 'popular' ? 'bg-orange-100 text-orange-600' :
                'bg-slate-100 text-slate-500'
              }`}>
                {getTypeLabel(suggestion.type)}
              </span>
            </button>
          ))}
          
          {/* Search all button */}
          {query.length >= 2 && (
            <button
              onClick={() => handleSearch()}
              className="w-full flex items-center gap-3 px-4 py-3 text-left bg-slate-50 hover:bg-slate-100 border-t transition-colors"
            >
              <Search className="w-4 h-4 text-blue-600" />
              <span className="text-blue-600">Buscar "{query}"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
